import { useEffect, useMemo, useRef, useState } from 'react'
import { IconCalendar, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight } from './icons'
import { Button } from './ui'
import { istDayOf } from '../lib/ist'
import { DEFAULT_ACADEMIC, anchoredSpan, spanDays as daysBetween, type AcademicCalendar, type RangeAnchor } from './periods'

/**
 * The console's period picker (Figma: Components › Date Range / Date Range
 * Panel, K15b) — presets on the left, a two-month calendar for a custom span
 * on the right, and a footer with an optional "Compare with previous N days"
 * switch and Cancel / Apply. Same shape as the Vibely console's, which the
 * redesign took as its reference.
 *
 * ⚠️ THE VALUE IS TWO `YYYY-MM-DD` STRINGS, INCLUSIVE, because that is what
 * every screen and endpoint in this product already speaks (`DateRange` in
 * school/dashboardRange.ts).
 *
 * 🔴 NOTHING APPLIES UNTIL "APPLY" (Figma K15b, MASTER-PLAN P2-20). A preset or
 * a calendar span only changes a DRAFT inside the open panel; Apply commits it
 * (and the compare switch with it), while Cancel, Escape and a click outside
 * throw it away. Every screen behind this picker refetches on a change, so a
 * picker that applied on each click fired a query per exploratory click — and
 * an operator comparing "Month to date" with "This term" paid for both.
 */

/**
 * A preset is either a count of days back from today (`days: 0` is the "All
 * time" sentinel) or a calendar ANCHOR — "Week to date", "This term" — whose
 * start is a date rather than a count (`periods.ts`).
 *
 * ⚠️ THE OTHER KEY IS TYPED `?: undefined`, NOT LEFT OFF, so `p.days` and
 * `p.anchor` stay readable on the union and the `{ label, days }` arrays every
 * screen already passes keep type-checking unchanged.
 */
export interface DaysPreset {
  label: string
  days: number
  anchor?: undefined
}

export interface AnchorPreset {
  label: string
  anchor: RangeAnchor
  days?: undefined
}

export type RangePreset = DaysPreset | AnchorPreset

export const DEFAULT_PRESETS: readonly RangePreset[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 12 months', days: 365 },
]

/**
 * "No bounds" — `days: 0` is the sentinel. Choosing it calls `onChange('', '')`,
 * and a picker given `from === '' && to === ''` reads "All time".
 *
 * ⚠️ OPT-IN. It is not in `DEFAULT_PRESETS`, so a screen whose endpoint needs a
 * window never receives empty strings; a list of records (invoices, tickets,
 * audit) passes `LIST_PRESETS` and sends no `from`/`to` while it is chosen —
 * see `rangeQuery`. It is also dropped whenever `maxDays` is set.
 */
export const ALL_TIME: RangePreset = { label: 'All time', days: 0 }

/** The presets for a list of records: "All time" first, then the usual spans. */
export const LIST_PRESETS: readonly RangePreset[] = [ALL_TIME, ...DEFAULT_PRESETS]

/**
 * Figma K15b's eleven periods, in its order. "This term" and "This academic
 * year" read the picker's `calendar` (D21 defaults: June start, Jun–Oct and
 * Nov–Mar terms).
 */
export const OVERVIEW_PRESETS: readonly RangePreset[] = [
  { label: 'Last 24 hours', days: 1 },
  { label: 'Week to date', anchor: 'wtd' },
  { label: 'Month to date', anchor: 'mtd' },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 12 months', days: 365 },
  { label: 'This term', anchor: 'term' },
  { label: 'This academic year', anchor: 'ay' },
  ALL_TIME,
]

/** A picker value: two inclusive `YYYY-MM-DD` days, or both `''` for all time. */
export interface DaySpan {
  from: string
  to: string
}

export const ALL_TIME_SPAN: DaySpan = { from: '', to: '' }

export function isAllTime(r: DaySpan): boolean {
  return r.from === '' && r.to === ''
}

/** The query an endpoint takes for a span: nothing at all for "All time". */
export function rangeQuery(r: DaySpan): { from?: string; to?: string } {
  return isAllTime(r) ? {} : { from: r.from, to: r.to }
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/**
 * A CALENDAR `Date` (one made by `fromDay`, or a calendar cell) as `YYYY-MM-DD`.
 *
 * ⚠️ NOT FOR "TODAY". It reads the Date's local parts, which is right for a
 * calendar day that was built in local parts and wrong for an instant: on a
 * machine outside India `toDay(new Date())` names the laptop's day, not
 * India's. Use `istDayOf(now)` (`shared/lib/ist.ts`) for anything that means
 * "today" or "the day this happened" — `presetSpan` and the calendar below do.
 */
export function toDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${String(d.getFullYear())}-${m}-${day}`
}

export function fromDay(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

/**
 * The `days`-long span ending TODAY IN INDIA, inclusive.
 *
 * 🔴 IST, NOT THE BROWSER'S DAY (MASTER-PLAN §3 F8). The day key comes from
 * `istDayOf(now)`; only the counting back is done on a calendar `Date`, which
 * is safe because it starts from local midnight of that IST date.
 */
export function presetSpan(days: number, now: Date = new Date()): { from: string; to: string } {
  const to = istDayOf(now)
  const from = fromDay(to)
  from.setDate(from.getDate() - (days - 1))
  return { from: toDay(from), to }
}

function spanDays(from: string, to: string): number {
  return Math.round((fromDay(to).getTime() - fromDay(from).getTime()) / 86_400_000) + 1
}

/**
 * "10 Sep – 9 Oct". The year is shown when the span crosses one, and — given
 * `now` — when it lies in another year, so last year's January never reads as
 * this year's.
 */
export function shortSpan(from: string, to: string, now?: Date): string {
  const a = fromDay(from)
  const b = fromDay(to)
  const year = a.getFullYear() !== b.getFullYear() || (now !== undefined && b.getFullYear() !== now.getFullYear())
  const f = (d: Date) => `${String(d.getDate())} ${SHORT[d.getMonth()] ?? ''}${year ? ` ${String(d.getFullYear())}` : ''}`
  return `${f(a)} – ${f(b)}`
}

/* ── Presets, as data ────────────────────────────────────────────────────── */

/** The span a preset stands for today: All time, N days back, or an anchored period. */
export function presetRange(p: RangePreset, now: Date = new Date(), cal: AcademicCalendar = DEFAULT_ACADEMIC): DaySpan {
  if (p.anchor !== undefined) return anchoredSpan(p.anchor, now, cal)
  return p.days === 0 ? ALL_TIME_SPAN : presetSpan(p.days, now)
}

/**
 * The presets a screen may offer. With `maxDays` (an endpoint that needs a
 * bounded window) "All time" goes, and so does any span longer than that today
 * — an anchored one is measured on today's calendar, so "This academic year"
 * may be offered in July and not in March.
 */
export function usablePresets(
  presets: readonly RangePreset[],
  maxDays: number | undefined,
  now: Date = new Date(),
  cal: AcademicCalendar = DEFAULT_ACADEMIC,
): RangePreset[] {
  if (maxDays === undefined) return [...presets]
  return presets.filter((p) => {
    if (p.anchor !== undefined) {
      const s = anchoredSpan(p.anchor, now, cal)
      return daysBetween(s.from, s.to) <= maxDays
    }
    return p.days > 0 && p.days <= maxDays
  })
}

/**
 * The preset a span is, for the label and the check mark: `prefer` (the one
 * last chosen) when it still gives this span, else the first in list order —
 * on a Monday "Last 24 hours" and "Week to date" are the same day, and the
 * one the operator actually picked is the one they expect to read back.
 */
export function matchPreset(
  presets: readonly RangePreset[],
  span: DaySpan,
  now: Date = new Date(),
  cal: AcademicCalendar = DEFAULT_ACADEMIC,
  prefer?: RangePreset,
): RangePreset | undefined {
  const same = (p: RangePreset): boolean => {
    const s = presetRange(p, now, cal)
    return s.from === span.from && s.to === span.to
  }
  if (prefer && presets.includes(prefer) && same(prefer)) return prefer
  return presets.find(same)
}

/* ── The staged draft ────────────────────────────────────────────────────── */

/** What the open panel holds until Apply. */
export interface RangeDraft {
  from: string
  to: string
  /** The preset the draft span came from; undefined for a calendar span. */
  preset: RangePreset | undefined
  compare: boolean
  /** A first calendar day is chosen and the last is not — Apply waits. */
  picking: boolean
  /** The span was changed since the panel opened. */
  touched: boolean
}

export type RangeDraftAction =
  | { type: 'preset'; preset: RangePreset; span: DaySpan }
  | { type: 'pick'; from: string; to: string }
  | { type: 'picking'; on: boolean }
  | { type: 'compare'; on: boolean }

export function openDraft(from: string, to: string, compare = false): RangeDraft {
  return { from, to, preset: undefined, compare, picking: false, touched: false }
}

/** Pure, so what the panel commits is tested without a browser. */
export function reduceDraft(d: RangeDraft, a: RangeDraftAction): RangeDraft {
  switch (a.type) {
    case 'preset':
      return { ...d, from: a.span.from, to: a.span.to, preset: a.preset, picking: false, touched: true }
    case 'pick':
      return { ...d, from: a.from, to: a.to, preset: undefined, picking: false, touched: true }
    case 'picking':
      return { ...d, picking: a.on }
    case 'compare':
      return { ...d, compare: a.on }
  }
}

export function canApply(d: RangeDraft): boolean {
  return !d.picking
}

/**
 * What Apply reports: the span (only when it was changed — re-applying the
 * same view must not refetch or rewrite the URL) and the compare switch (only
 * when it was flipped).
 */
export function commitDraft(
  d: RangeDraft,
  initialCompare: boolean,
): { range: { from: string; to: string; preset: RangePreset | undefined } | null; compare: boolean | null } {
  return {
    range: d.touched ? { from: d.from, to: d.to, preset: d.preset } : null,
    compare: d.compare !== initialCompare ? d.compare : null,
  }
}

/** "Compare with previous 30 days" / "… previous day". */
export function compareLabel(span: DaySpan): string {
  const n = daysBetween(span.from, span.to)
  return n === 1 ? 'Compare with previous day' : `Compare with previous ${String(n)} days`
}

/* ── The picker ──────────────────────────────────────────────────────────── */

export function DateRangeButton({
  from,
  to,
  onChange,
  presets = DEFAULT_PRESETS,
  maxDays,
  now: nowProp,
  compare = false,
  onCompareChange,
  calendar = DEFAULT_ACADEMIC,
}: {
  from: string
  to: string
  /** Called on Apply. `preset` is the preset the span came from (undefined for a calendar pick). */
  onChange: (from: string, to: string, preset?: RangePreset) => void
  presets?: readonly RangePreset[]
  /** Longest span the endpoint behind the page accepts. */
  maxDays?: number
  now?: Date
  /** Whether the screen compares with the period before (the panel's switch). */
  compare?: boolean
  /** Shows the "Compare with previous N days" switch; called on Apply when it was flipped. */
  onCompareChange?: (on: boolean) => void
  /** The academic year behind "This term" / "This academic year". */
  calendar?: AcademicCalendar
}) {
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  /* null = closed. The draft is made when the panel opens, so discarding it is just closing. */
  const [draft, setDraft] = useState<RangeDraft | null>(null)
  /* Bumped on a preset so the calendar remounts onto the new span (and drops a half-made pick). */
  const [calKey, setCalKey] = useState(0)
  const [chosen, setChosen] = useState<RangePreset | undefined>(undefined)
  const root = useRef<HTMLDivElement>(null)
  const open = draft !== null

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setDraft(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDraft(null)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const dispatch = (a: RangeDraftAction) => {
    setDraft((d) => (d ? reduceDraft(d, a) : d))
  }

  const apply = () => {
    if (!draft || !canApply(draft)) return
    const c = commitDraft(draft, compare)
    if (c.range) {
      setChosen(c.range.preset)
      onChange(c.range.from, c.range.to, c.range.preset)
    }
    if (c.compare !== null) onCompareChange?.(c.compare)
    setDraft(null)
  }

  const usable = usablePresets(presets, maxDays, now, calendar)
  const all = isAllTime({ from, to })
  const matched = matchPreset(usable, { from, to }, now, calendar, chosen)
  const label = matched ? matched.label : all ? ALL_TIME.label : `${String(spanDays(from, to))} days`
  const spanText = all ? '' : shortSpan(from, to, now)
  const draftMatch = draft ? matchPreset(usable, draft, now, calendar, draft.preset ?? (draft.touched ? undefined : chosen)) : undefined

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={spanText ? `Period: ${label}, ${spanText}` : `Period: ${label}`}
        onClick={() => {
          setDraft((d) => (d ? null : openDraft(from, to, compare)))
        }}
        className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-line bg-surface pr-2 pl-2.5 font-dash text-[13px] text-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)] hover:bg-surface-subtle"
      >
        <IconCalendar size={16} className="text-ink-soft" />
        <span className="font-medium">{label}</span>
        {spanText ? <span className="text-[12px] text-muted">{spanText}</span> : null}
        <IconChevronDown size={14} className="text-muted" />
      </button>
      {draft ? (
        <div
          role="dialog"
          aria-label="Choose a period"
          className="absolute top-full right-0 z-40 mt-1.5 flex flex-col rounded-xl border border-line bg-surface font-dash shadow-raised"
        >
          <div className="flex">
            <div role="listbox" aria-label="Preset periods" className="flex w-[168px] flex-col gap-0.5 border-r border-line p-1.5">
              {usable.map((p) => {
                const on = p === draftMatch
                return (
                  <button
                    key={p.label}
                    type="button"
                    role="option"
                    aria-selected={on}
                    onClick={() => {
                      dispatch({ type: 'preset', preset: p, span: presetRange(p, now, calendar) })
                      setCalKey((k) => k + 1)
                    }}
                    className={`flex h-7 items-center justify-between rounded-md px-2.5 text-left text-[13px] text-ink hover:bg-surface-muted ${on ? 'bg-surface-muted font-medium' : ''}`}
                  >
                    {p.label}
                    {on ? <IconCheck size={14} /> : null}
                  </button>
                )
              })}
            </div>
            <RangeCalendar
              key={calKey}
              from={draft.from}
              to={draft.to}
              now={now}
              maxDays={maxDays}
              onPicking={(on) => {
                dispatch({ type: 'picking', on })
              }}
              onPick={(f, t) => {
                dispatch({ type: 'pick', from: f, to: t })
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-line px-3 py-2.5">
            {onCompareChange && !isAllTime(draft) ? (
              <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-ink">
                <input
                  type="checkbox"
                  checked={draft.compare}
                  onChange={(e) => {
                    dispatch({ type: 'compare', on: e.target.checked })
                  }}
                  className="h-4 w-4 accent-[var(--color-ink)]"
                />
                {compareLabel(draft)}
              </label>
            ) : (
              <span className="text-[12px] text-muted">{isAllTime(draft) ? 'All time' : shortSpan(draft.from, draft.to, now)}</span>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setDraft(null)
                }}
              >
                Cancel
              </Button>
              <Button size="sm" variant="primary" disabled={!canApply(draft)} onClick={apply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function RangeCalendar({
  from,
  to,
  now,
  maxDays,
  onPick,
  onPicking,
}: {
  from: string
  to: string
  now: Date
  maxDays?: number
  onPick: (from: string, to: string) => void
  /** A first day is chosen (true) or the pick finished / was reset (false). */
  onPicking: (on: boolean) => void
}) {
  /* Today in India, as a calendar Date — future days are disabled from here,
     and the dot under "today" marks India's day, not the laptop's. */
  const today = fromDay(istDayOf(now))
  /* "All time" has no bounds to draw: open on this month, nothing highlighted. */
  const unbounded = from === '' || to === ''
  const end = unbounded ? today : fromDay(to)
  const [right, setRight] = useState(() => new Date(end.getFullYear(), end.getMonth(), 1))
  const [anchor, setAnchor] = useState<Date | null>(null)
  const [hover, setHover] = useState<Date | null>(null)

  const span: { a: Date | null; b: Date | null } = anchor
    ? hover
      ? { a: anchor < hover ? anchor : hover, b: anchor < hover ? hover : anchor }
      : { a: anchor, b: anchor }
    : unbounded
      ? { a: null, b: null }
      : { a: fromDay(from), b: end }

  const canNext = right < new Date(today.getFullYear(), today.getMonth(), 1)
  const tooLong = (d: Date) =>
    anchor !== null && maxDays !== undefined && Math.abs(d.getTime() - anchor.getTime()) / 86_400_000 + 1 > maxDays

  const pick = (d: Date) => {
    if (!anchor) {
      setAnchor(d)
      onPicking(true)
      return
    }
    const [a, b] = anchor < d ? [anchor, d] : [d, anchor]
    onPick(toDay(a), toDay(b))
    setAnchor(null)
    setHover(null)
  }

  const months = [new Date(right.getFullYear(), right.getMonth() - 1, 1), right]

  return (
    <div className="p-3" onMouseLeave={() => { setHover(null) }}>
      <div className="flex gap-6">
        {months.map((month, i) => {
          const first = new Date(month.getFullYear(), month.getMonth(), 1)
          const daysIn = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
          const cells: (Date | null)[] = [
            ...Array.from({ length: first.getDay() }, () => null),
            ...Array.from({ length: daysIn }, (_, k) => new Date(month.getFullYear(), month.getMonth(), k + 1)),
          ]
          while (cells.length % 7) cells.push(null)
          const title = `${MONTHS[month.getMonth()] ?? ''} ${String(month.getFullYear())}`
          return (
            <div key={title} className="w-[238px]">
              <div className="mb-2 flex h-7 items-center">
                {i === 0 ? (
                  <button type="button" aria-label="Previous month" onClick={() => { setRight(new Date(right.getFullYear(), right.getMonth() - 1, 1)) }} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-muted hover:text-ink">
                    <IconChevronLeft size={16} />
                  </button>
                ) : null}
                <span className="flex-1 text-center text-[13px] font-semibold text-ink">{title}</span>
                {i === 1 ? (
                  <button type="button" aria-label="Next month" disabled={!canNext} onClick={() => { setRight(new Date(right.getFullYear(), right.getMonth() + 1, 1)) }} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-surface-muted hover:text-ink disabled:opacity-30">
                    <IconChevronRight size={16} />
                  </button>
                ) : null}
              </div>
              <div role="grid" aria-label={title} className="grid grid-cols-7 gap-y-0.5 text-center">
                {WEEKDAYS.map((d) => (
                  <span key={d} className="py-1 text-[11.5px] font-medium text-muted">{d}</span>
                ))}
                {cells.map((day, k) => {
                  if (!day) return <span key={`b${String(k)}`} />
                  const disabled = day > today || tooLong(day)
                  const inSpan = span.a !== null && span.b !== null && day >= span.a && day <= span.b
                  const isStart = sameDay(day, span.a)
                  const isEnd = sameDay(day, span.b)
                  const endpoint = isStart || isEnd || sameDay(day, anchor)
                  return (
                    <span
                      key={day.toISOString()}
                      className={`flex justify-center ${inSpan && !(isStart && isEnd) ? 'bg-surface-muted' : ''} ${isStart && !isEnd ? 'rounded-l-full' : ''} ${isEnd && !isStart ? 'rounded-r-full' : ''}`}
                    >
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={`${MONTHS[day.getMonth()] ?? ''} ${String(day.getDate())}, ${String(day.getFullYear())}`}
                        aria-pressed={endpoint}
                        onClick={() => { pick(day) }}
                        onMouseEnter={() => { setHover(day) }}
                        className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full text-[13px] tabular-nums ${
                          endpoint ? 'bg-primary font-semibold text-primary-ink' : 'text-ink hover:ring-1 hover:ring-line-strong'
                        } ${disabled ? 'cursor-default text-muted/40 hover:ring-0' : ''}`}
                      >
                        {day.getDate()}
                        {sameDay(day, today) && !endpoint ? (
                          <span aria-hidden="true" className="absolute bottom-1 h-1 w-1 rounded-full bg-ink" />
                        ) : null}
                      </button>
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-2 px-1 text-[11.5px] text-muted">
        {anchor ? 'Pick the last day' : 'Click a first day, then a last day'}
        {maxDays !== undefined ? ` · up to ${String(maxDays)} days` : ''}
      </p>
    </div>
  )
}
